// In-place viewpoint switch for replay playback.
//
// The seat (`isHost`) drives the camera, the 3D name badges, ownership
// outlines, which seat's hand renders at the bottom and which way the
// ATK/HP pills face. Live matches resolve it once at mount. A replay used
// to switch sides by REMOUNTING the asset gate and the whole board keyed on
// the viewpoint: assets re-preloaded, the scene rebuilt, the replay
// re-folded from scratch, several seconds behind a loading overlay for what
// is the same data seen from the other chair (QA#151).
//
// Everything seat-dependent is re-derivable from live state: the fold
// funnel reads host.state.isHost at fold time, and applySeatPerspective
// already re-applies camera + badges + outlines for a live seat flip. So a
// viewpoint change flips the seat and re-folds, nothing else.

import { updateCounterHUDOrientation, updateLifeHUDOrientation } from '../../../utils/game/cardMesh';

/** True when the viewpoint prop changed on a mounted replay board. */
export function replayViewpointChanged(host, prevProps) {
  return !!host.props.replayDriver && prevProps.replayViewpoint !== host.props.replayViewpoint;
}

export function applyReplayViewpoint(host, viewpoint) {
  const driver = host.props.replayDriver;
  if (!driver) return false;
  const isHost = viewpoint !== 'p2';
  if (host.state.isHost === isHost) return false;

  // The badges are built once per name and never re-labelled; the seats'
  // identities swap with the viewpoint, so rebuild them from the new props.
  host._disposeBadge?.(host._playerBadgeMesh);
  host._disposeBadge?.(host._opponentBadgeMesh);
  host._playerBadgeMesh = null;
  host._opponentBadgeMesh = null;
  // The opponent fan folds by signature; null (never undefined, which
  // re-seeds from the current fan) forces the other seat's hand to build.
  host._oppHandSig = null;

  // Clearing the bottom hand lets the Full below rebuild it for the new
  // seat instead of healing the old seat's cards into place.
  host.setState({ isHost, handCards: [] }, () => {
    if (host._unmounted) return;
    host.applySeatPerspective(isHost);
    for (const [id, hud] of host.lifeHUDs || []) {
      const card = host.meshes?.get(id)?.userData?.cardInstance;
      if (card) updateLifeHUDOrientation(hud, card, isHost);
    }
    for (const [id, hud] of host.counterHUDs || []) {
      const card = host.meshes?.get(id)?.userData?.cardInstance;
      if (card) updateCounterHUDOrientation(hud, card, isHost);
    }
    driver.requestFull();
  });
  return true;
}
